import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSearch } from '../hooks/useSearch';
import Layout from '../components/layout/Layout';
import { Button, Avatar, ErrorState } from '../components/ui';
import PremiumName from '../components/ui/PremiumName';
import { PostCard, MasonryGrid } from '../components/posts';
import { ServiceCard, ServiceModal } from '../components/services';
import { PostSuggestion, ServiceSuggestion } from '../components/search';
import { db } from '../firebase';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { normalizeService, searchPosts, allWordsPresent } from '../services/postService';

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const q = searchParams.get('q') || '';
  const [inputValue, setInputValue] = useState(q);
  const inputValueRef = useRef(inputValue);
  const [suggestions, setSuggestions] = useState({ users: [], posts: [], services: [] });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef(null);

  inputValueRef.current = inputValue;

  useEffect(() => {
    setInputValue(q);
  }, [q]);

  const fetchSuggestions = useCallback(async (searchTerm) => {
    const term = (searchTerm || '').trim();
    if (!term) {
      setSuggestions({ users: [], posts: [], services: [] });
      setShowSuggestions(false);
      return;
    }
    try {
      const firstWord = term.trim().toLowerCase().split(/\s+/)[0] || '';
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const foundUsers = usersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(user => {
          const username = user.username || '';
          const displayName = user.displayName || '';
          const email = user.email || '';
          const combined = `${username} ${displayName} ${email}`;
          return allWordsPresent(combined, term);
        })
        .sort((a, b) => {
          const aName = (a.username || a.displayName || '').toLowerCase();
          const bName = (b.username || b.displayName || '').toLowerCase();
          const aStarts = aName.startsWith(firstWord);
          const bStarts = bName.startsWith(firstWord);
          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;
          return 0;
        })
        .slice(0, 5);

      const [postsResult, servicesSnapshot] = await Promise.all([
        searchPosts(term, 5),
        getDocs(query(collection(db, 'services'), orderBy('createdAt', 'desc'), limit(20)))
      ]);
      const foundPosts = postsResult.success ? (postsResult.posts || []).slice(0, 5) : [];
      const foundServices = servicesSnapshot.docs
        .map(doc => normalizeService({ id: doc.id, ...doc.data() }))
        .filter(service => {
          const caption = service.caption || '';
          const description = service.description || '';
          const category = service.categoryName || service.category || '';
          const combined = `${caption} ${description} ${category}`;
          return allWordsPresent(combined, term);
        })
        .slice(0, 5);

      if (inputValueRef.current !== term) return;
      setSuggestions({ users: foundUsers, posts: foundPosts, services: foundServices });
      setShowSuggestions(true);
    } catch (err) {
      if (inputValueRef.current !== term) return;
      console.error('Error al buscar sugerencias:', err);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue.trim()) fetchSuggestions(inputValue);
      else {
        setSuggestions({ users: [], posts: [], services: [] });
        setShowSuggestions(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [inputValue, fetchSuggestions]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showSuggestions && searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSuggestions]);

  const { userData } = useAuth();
  const { users, posts, services, loading, error } = useSearch(q);
  const [selectedService, setSelectedService] = useState(null);

  const blockedIds = userData?.blocked || [];
  const filteredUsers = users.filter(u => !blockedIds.includes(u.id));
  const filteredPosts = posts.filter(p => !blockedIds.includes(p.userId || p.authorId));
  const filteredServices = services.filter(s => !blockedIds.includes(s.userId));

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    const term = (inputValue || '').trim();
    if (term) {
      navigate(`/search?q=${encodeURIComponent(term)}`);
    }
  };

  const hasAnyResults = filteredUsers.length > 0 || filteredPosts.length > 0 || filteredServices.length > 0;

  return (
    <Layout>
      <div className="py-6 container-custom">
        {/* Barra de búsqueda con sugerencias */}
        <form onSubmit={handleSearchSubmit} className="mb-8" ref={searchContainerRef}>
          <div className="relative flex gap-3">
            <input
              type="text"
              placeholder="Buscar usuarios, posteos o servicios..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onFocus={() => {
                if (inputValue.trim() && (suggestions.users.length + suggestions.posts.length + suggestions.services.length > 0)) {
                  setShowSuggestions(true);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearchSubmit();
                if (e.key === 'Escape') setShowSuggestions(false);
              }}
              className="flex-1 px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <Button type="submit" variant="gradient">
              Buscar
            </Button>
            {showSuggestions && (suggestions.users.filter(u => !blockedIds.includes(u.id)).length + suggestions.posts.filter(p => !blockedIds.includes(p.userId || p.authorId)).length + suggestions.services.filter(s => !blockedIds.includes(s.userId)).length > 0) && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl max-h-96 overflow-y-auto z-50">
                {suggestions.users.filter(u => !blockedIds.includes(u.id)).length > 0 && (
                  <div className="p-2">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 py-2">Usuarios</p>
                    {suggestions.users.filter(u => !blockedIds.includes(u.id)).map(user => (
                      <Link
                        key={user.id}
                        to={`/profile/${user.id}`}
                        onClick={() => setShowSuggestions(false)}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                      >
                        <Avatar
                          src={user.photoURL || user.profileImage}
                          alt={user.username || user.displayName}
                          fallbackText={user.username || user.displayName}
                          size="sm"
                        />
                        <div className="flex-1 min-w-0">
                          <PremiumName
                            name={user.username || user.displayName || 'Usuario'}
                            isPremium={user.isPremium}
                            premiumColor={user.premiumNameColor}
                            nameClassName="text-sm font-medium text-gray-900 dark:text-white truncate block"
                          />
                          {user.profession && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.profession}</p>}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
                {suggestions.posts.filter(p => !blockedIds.includes(p.userId || p.authorId)).length > 0 && (
                  <div className="p-2 border-t border-gray-200 dark:border-slate-700">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 py-2">Posteos</p>
                    {suggestions.posts.filter(p => !blockedIds.includes(p.userId || p.authorId)).map(post => (
                      <PostSuggestion key={post.id} post={post} onClick={() => { navigate(`/post/${post.id}`); setShowSuggestions(false); }} />
                    ))}
                  </div>
                )}
                {suggestions.services.filter(s => !blockedIds.includes(s.userId)).length > 0 && (
                  <div className="p-2 border-t border-gray-200 dark:border-slate-700">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 py-2">Servicios</p>
                    {suggestions.services.filter(s => !blockedIds.includes(s.userId)).map(service => (
                      <ServiceSuggestion key={service.id} service={service} onClick={() => { setSelectedService(service); setShowSuggestions(false); }} />
                    ))}
                  </div>
                )}
                <div className="p-2 border-t border-gray-200 dark:border-slate-700">
                  <button type="button" onClick={() => { handleSearchSubmit(); setShowSuggestions(false); }} className="w-full px-3 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors font-medium">
                    Ver todos los resultados para &quot;{inputValue}&quot;
                  </button>
                </div>
              </div>
            )}
          </div>
          </form>

        {!q.trim() && (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 dark:bg-slate-800 rounded-full mb-4">
              <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Escribe algo para buscar
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Busca usuarios, posteos y servicios desde la barra de arriba.
            </p>
            <Button variant="outline" onClick={() => navigate('/')}>
              Volver a Explorar
            </Button>
          </div>
        )}

        {q.trim() && error && (
          <ErrorState title="No se pudo buscar" message={error} onAction={() => navigate(`/search?q=${encodeURIComponent(q)}`)} />
        )}

        {q.trim() && loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
          </div>
        )}

        {q.trim() && !loading && !error && (
          <>
            {/* Usuarios */}
            <section className="mb-10">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Usuarios ({filteredUsers.length})
              </h2>
              {filteredUsers.length > 0 ? (
                <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory">
                  {filteredUsers.map(user => (
                    <Link
                      key={user.id}
                      to={`/profile/${user.id}`}
                      className="flex-shrink-0 w-48 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4 hover:shadow-lg transition-all snap-start"
                    >
                      <div className="flex flex-col items-center text-center">
                        <Avatar
                          src={user.photoURL || user.profileImage}
                          alt={user.username || user.displayName}
                          fallbackText={user.username || user.displayName}
                          size="lg"
                          className="mb-3"
                        />
                        <PremiumName
                          name={user.username || user.displayName || 'Usuario'}
                          isPremium={user.isPremium}
                          premiumColor={user.premiumNameColor}
                          className="w-full justify-center"
                          nameClassName="font-semibold text-gray-900 dark:text-white truncate block w-full"
                        />
                        {user.profession && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate w-full">
                            {user.profession}
                          </p>
                        )}
                        {user.followersCount !== undefined && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                            {user.followersCount} seguidores
                          </p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 py-4">
                  No hay usuarios para esta búsqueda.
                </p>
              )}
            </section>

            {/* Servicios */}
            <section className="mb-10">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Servicios ({filteredServices.length})
              </h2>
              {filteredServices.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
                  {filteredServices.map(service => (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      onClick={setSelectedService}
                      currentUserId={userData?.id}
                      savedServiceIds={userData?.savedServices || []}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 py-4">
                  No hay servicios para esta búsqueda.
                </p>
              )}
            </section>

            {/* Posteos */}
            <section className="mb-10">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Posteos ({filteredPosts.length})
              </h2>
              {filteredPosts.length > 0 ? (
                <MasonryGrid>
                  {filteredPosts.map(post => (
                    <PostCard
                      key={post.id}
                      post={post}
                      currentUserId={userData?.id}
                      savedPostIds={userData?.savedPosts || []}
                    />
                  ))}
                </MasonryGrid>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 py-4">
                  No hay posteos para esta búsqueda.
                </p>
              )}
            </section>

            {!hasAnyResults && (
              <div className="text-center py-12">
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  No hay resultados para &quot;{q}&quot;
                </p>
                <Button variant="outline" onClick={() => navigate('/')}>
                  Volver a Explorar
                </Button>
              </div>
            )}
          </>
        )}

        {q.trim() && !loading && (
          <div className="mt-8">
            <Button variant="outline" onClick={() => navigate('/')}>
              Volver a Explorar
            </Button>
          </div>
        )}
      </div>

      {selectedService && (
        <ServiceModal
          service={selectedService}
          onClose={() => setSelectedService(null)}
        />
      )}
    </Layout>
  );
};

export default SearchPage;
